import ApiService, { IRequestParameters } from '@/plugins/api-plugin/services/api.service'
import SignInDto from '@/interfaces/common/SignIn.dto'
import { PageNumberPagination } from '@/interfaces/common/PageNumberPagination'
import MyAspnetUsersDao from '@/interfaces/user/MyAspnetUsers.dao'
import MyAspnetUserDao from '@/interfaces/user/MyAspnetUser.dao'
import { UserSearchFiltersDao } from '@/interfaces/user/UserSearchFilters.dao'
import { UserSearchFiltersDto } from '@/interfaces/user/UserSearchFilters.dto'
import { EditUserSearchFiltersDto } from '@/interfaces/user/EditUserSearchFilters.dto'

export interface TokensInterface {
    accessToken: string
    refreshToken: string
}

export default class UserService extends ApiService {
    async signIn(email: string, password: string): Promise<TokensInterface | null> {
        return this.request<SignInDto, TokensInterface>(
            'POST',
            `/auth/signIn`,
            undefined,
            { email, password },
            undefined,
            'signIn',
        ).catch(() => null)
    }

    async getMyAspnetUsers(
        params?: IRequestParameters,
        preventCancel = false,
    ): Promise<[MyAspnetUsersDao[], PageNumberPagination] | null> {
        return this.request<undefined, [MyAspnetUsersDao[], PageNumberPagination]>(
            'GET',
            '/users/my-aspnet-users',
            params,
            undefined,
            undefined,
            preventCancel ? '' : 'getMyAspnetUsers',
        ).catch(() => null)
    }

    async getMyAspnetUser(myAspnetUserId: string): Promise<MyAspnetUserDao | null> {
        return this.request<undefined, MyAspnetUserDao>(
            'GET',
            `/users/my-aspnet-users/${encodeURIComponent(myAspnetUserId)}`,
            undefined,
            undefined,
            undefined,
            'getMyAspnetUser',
        ).catch(() => null)
    }

    async resetPassword(id: number, password?: string): Promise<{ password: string } | null> {
        return this.request<{ password?: string }, { password: string } | null>(
            'POST',
            `/users/${encodeURIComponent(id)}/reset-password`,
            undefined,
            password ? { password } : undefined,
            undefined,
            'resetPassword',
        ).catch(() => null)
    }

    async resendWelcomeEmail(
        id: number,
        payload: { ccEmails?: string[]; bccEmails?: string[] },
    ): Promise<{ password: string } | null> {
        return this.request<{ ccEmails?: string[]; bccEmails?: string[] }, { password: string } | null>(
            'POST',
            `/users/${encodeURIComponent(id)}/resend-welcome-email`,
            undefined,
            payload,
            undefined,
            'resendWelcomeEmail',
        ).catch(() => null)
    }

    async getUserSearchFilters<T>(
        params?: IRequestParameters,
    ): Promise<[UserSearchFiltersDao<T>[], PageNumberPagination] | null> {
        return this.request<undefined, [UserSearchFiltersDao<T>[], PageNumberPagination]>(
            'GET',
            '/users/user-search-filters',
            params,
            undefined,
            undefined,
            'getUserSearchFilters',
        ).catch(() => null)
    }

    async createUserSearchFilters<T>(
        createUserSearchFilterDto: UserSearchFiltersDto<T>,
    ): Promise<UserSearchFiltersDao<any> | null> {
        return this.request<UserSearchFiltersDto<T>, UserSearchFiltersDao<T>>(
            'POST',
            '/users/user-search-filters',
            undefined,
            createUserSearchFilterDto,
            undefined,
            'createUserSearchFilters',
        ).catch(() => null)
    }

    async updateUserSearchFilters<T>(
        updateUserSearchFilterDto: EditUserSearchFiltersDto<T>,
    ): Promise<UserSearchFiltersDao<T> | null> {
        return this.request<EditUserSearchFiltersDto<T>, UserSearchFiltersDao<T>>(
            'PUT',
            `/users/user-search-filters/`,
            undefined,
            updateUserSearchFilterDto,
            undefined,
            'updateUserSearchFilters',
        ).catch(() => null)
    }

    async deleteUserSearchFilters<T>(id: number): Promise<UserSearchFiltersDao<T> | null> {
        return this.request<undefined, UserSearchFiltersDao<T>>(
            'DELETE',
            `/users/user-search-filters/${encodeURIComponent(id)}`,
            undefined,
            undefined,
            undefined,
            'deleteUserSearchFilters',
        ).catch(() => null)
    }
}
